/**
 * QR-2, веб-срез В1: движок общей комнаты — отправка пачкой, догон по
 * разреженному ряду, доверие по каждому участнику. Экрана здесь нет: В1
 * отдаёт наверх готовый снимок состояния, В2 его рисует.
 *
 * 🔴 Отдельный контроллер, а не ветка в `chatController` (§1.3 дизайна). У
 * комнаты другой объект отправки (ПАЧКА копий вместо одного конверта), другой
 * курсор (на устройство, а не на принципала), другой догон (разреженный ряд с
 * `latest_seq`) и другой класс ошибок (`stale_roster`). Ветвление внутри
 * 622-строчного файла, где каждая ветка про 1:1, — способ уронить 1:1, который
 * работает на бою с 25.08.
 *
 * Инварианты среза (каждый оплачен разбором дизайна, менять только сознательно):
 * 1. `message_id` рождается ОДИН РАЗ и переживает все повторы; текст лежит в
 *    аутбоксе ДО первой попытки, поэтому перезагрузка вкладки его не теряет.
 * 2. Пачка целиком или никак: ровно одна копия на КАЖДОЕ обязательное
 *    устройство. Нет ключей у обязательного получателя — не отправляем вовсе
 *    (§4.1), а не шлём заведомо неполную пачку ради серверного 409.
 * 3. Повтор — ТОЛЬКО по `detail.code === 'stale_roster'`. Голым 409 отвечает
 *    и коллизия `message_id` другого отправителя: там повтор вечен и вреден.
 * 4. `410` в API комнаты не бывает вовсе: удалённая, чужая и несуществующая
 *    комната одинаково маскируются под 404.
 * 5. `duplicate: true` — это УСПЕХ (ack потерялся, сервер дедупнул).
 * 6. Догон кончается по `latest_seq`, а не по пустой странице: ряд номеров
 *    устройства разрежен, пустая страница посреди ряда — дыра, а не конец.
 * 7. Курсор уходит ПОСЛЕ того, как расшифрованное сохранено: сервер по курсору
 *    считает сообщения доставленными и отдаёт их ретеншну.
 */
import {
  attachmentMeta, decryptAttachment, encryptFile, MAX_ATTACHMENTS_PER_MESSAGE,
  validMeta,
} from './attachments';
import { normalizeReplyTo } from './replyTo';
// 🔴 Правило перевода отказов загрузки — ОДНО на оба контроллера (как
// trustBlocksSend): разошлись бы тексты и коды — 1:1 и комната объясняли бы
// один и тот же 413 по-разному. Цикла нет: chatController комнату не импортит.
import { uploadError } from './chatController';
import { MsgStore } from './msgStore';
import { deviceIdentityLostError, deviceTakeoverError } from './deviceTakeover';
import { blockedDeviceIds, principalKey, toDeviceEntries } from './deviceFanout';
import {
  newMessageId, SerialQueue, startStreamLoop, storeNameFor, storePassphrase,
  StoppedError, tofuVerdict, trustBlocksSend, trustSummary } from './msgRuntime';
import { ed25519Of } from './safetyNumber';

/** Страница догона. Сервер разрешает до 200, сотня — как в 1:1. */
export const CATCH_UP_PAGE = 100;

/**
 * МУ-1-1: состав устройств человека с ключами не изменился, пока клик стоял в
 * очереди. Устройства без ed25519 не считаются ни с одной стороны: сверять их
 * нечем, и они не имеют права ломать сверку соседнего нормального устройства
 * (ревью р.4, P2).
 */
function sameKeyedDevices(snapshot = [], current = []) {
  const a = (snapshot ?? []).filter((d) => d?.did && d.ed25519);
  const b = (current ?? []).filter((d) => d?.did && d.ed25519);
  return a.length === b.length
    && a.every((d) => b.some((c) => c.did === d.did && c.ed25519 === d.ed25519));
}

/**
 * Потолок повторов при `stale_roster`. Без него двое, входящие и выходящие по
 * очереди, дают бесконечный цикл; с ним сообщение доходит до человека как
 * «не отправлено» с кнопкой, а не крутится молча.
 */
export const MAX_STALE_RETRIES = 3;

/** Комнаты нет: владелец её ещё не открыл (участнику `listRooms` даёт пусто). */
export class NoRoomError extends Error {
  constructor() {
    super('Общий чат ещё не открыт');
    this.name = 'NoRoomError';
  }
}

/** Повтор осмыслен ТОЛЬКО здесь: ростер сменился между чтением и отправкой. */
export function isStaleRoster(e) {
  return e?.status === 409 && e?.detail?.code === 'stale_roster';
}

/**
 * Отказ отправки, после которого повторять нечего. Возвращает текст для
 * человека или null, если отказ восстановимый (сеть, 5xx).
 *
 * 🔴 Голый 409 сюда попадает намеренно: `stale_roster` отсеян ВЫШЕ, а всё
 * остальное с этим кодом — коллизия `message_id` чужого отправителя.
 */
export function terminalSendError(e) {
  if (e?.status === 409) {
    return 'Сервер отбил сообщение: такой идентификатор уже занят. '
      + 'Отправьте текст заново.';
  }
  if (e?.status === 404) {
    return 'Общего чата больше нет — сообщение не доставлено.';
  }
  if (e?.status === 422) {
    return 'Сервер не принял сообщение (размер или формат) — оно не доставлено.';
  }
  if (e?.status === 403) {
    return 'Это устройство больше не может писать в общий чат.';
  }
  return null;
}

/**
 * Отказ, после которого ЖДАТЬ нечего никому: комнаты нет (404) или это
 * устройство больше не вправе в неё писать (403).
 *
 * 🔴 Ровно на «вечном ожидании» сорвался прогон владельца 25.08 в переписке
 * 1:1: вкладка бесконечно ждала собеседника, которого уже удалили, и не
 * пускала никуда ещё. Отличать такой отказ от обрыва связи обязан КАЖДЫЙ
 * тракт, а не только отправка (ревью р.1, High).
 */
export function isFatal(e) {
  return e?.status === 404 || e?.status === 403;
}

/** Причина терминального отказа — она решает, что делать «повторить». */
export function reasonOf(e) {
  if (e?.status === 409) return 'id_collision';
  if (e?.status === 404) return 'room_gone';
  if (e?.status === 403) return 'forbidden';
  if (e?.status === 422) return 'rejected';
  return 'unknown';
}

/** Отказ подъёма, переведённый на человеческий. */
export function roomBootstrapError(e) {
  // Д-20: тот же вопрос, что и в 1:1 — узнаём по имени, не instanceof
  // (класс живёт в olmClient, который грузится динамически вместе с WASM).
  if (e?.name === 'DeviceTakeoverNeeded') return deviceTakeoverError(e.info);
  // Д-3 «хвост»: крипто-стор погиб, did цел. Комната упирается в это первой —
  // `publishKeys` идёт до резолва комнаты, то есть до любой её ошибки.
  if (e?.name === 'DeviceIdentityLost') return deviceIdentityLostError();
  if (e instanceof NoRoomError) {
    return {
      kind: 'no_room',
      text: 'Общий чат ещё не открыт — попросите владельца открыть его.',
      retriable: false,
    };
  }
  // 404 — и выключенный флаг комнат, и чужая, и удалённая комната: сервер
  // маскирует всё это одинаково намеренно, и различить их клиент не может
  if (e?.status === 404) {
    return { kind: 'no_room', text: 'Общий чат недоступен.', retriable: false };
  }
  if (e?.status === 403) {
    return {
      kind: 'forbidden',
      text: 'Нет доступа к общему чату с этого устройства.',
      retriable: false,
    };
  }
  return { text: `Крипта не поднялась: ${e?.message ?? e}`, retriable: false };
}

export class RoomController {
  /**
   * @param api        MsgApi
   * @param ownerId    id владельца из /website/me
   * @param did        browserDeviceId()
   * @param loadCrypto () => import('./olmClient') — единственная точка, тянущая
   *                   WASM-чанк; тесты подставляют фейк
   * @param onUpdate   (state) => void — снимок состояния для страницы
   * @param storeFactory для тестов
   * @param principal  КТО говорит в этой вкладке: {type, id}. По умолчанию —
   *                   владелец в своём ЛК; участник по приглашению приходит со
   *                   своим принципалом (QR-3, браузер без установки).
   */
  constructor({
    api, ownerId, did, loadCrypto, onUpdate, storeFactory = null, principal = null,
    allowTakeover = false,
  }) {
    this.api = api;
    this.principal = principal ?? { type: 'owner', id: ownerId };
    this.did = did;
    // Д-20: человек нажал «перенести сюда» — вытеснять чужое устройство можно.
    this.allowTakeover = allowTakeover;
    this.loadCrypto = loadCrypto ?? (() => import('./olmClient'));
    this.onUpdate = onUpdate ?? (() => {});
    this.storeFactory = storeFactory ?? (() => new MsgStore());
    this.client = null;
    this.store = null;
    this.room = null;
    this.stream = null;         // цикл SSE-подписки, поднимается после bootstrap
    this.stopped = false;
    this.queue = new SerialQueue(() => this.stopped);
    this._catchUpQueued = false;
    this._devices = [];
    this._names = {};
    // Максимальный номер комнаты, который мы уже видели: по нему системные
    // строки встают в хронологию, а не в её начало.
    this._seenSeq = 0;
    // Устаревшие device id из истории: по каждому чужому sender_device_id
    // ростер устройств перечитывается ОДИН раз за жизнь контроллера. Ключей
    // это чтение не жжёт (identity без one-time), но и цикл по истории с
    // мёртвого устройства нам не нужен.
    this._staleSenders = new Set();
    this.state = {
      phase: 'init',            // init | ready | error
      error: null,              // {text, retriable, messageId?, blocked?}
      room: null,               // {id, title, lastSeq, joinedAfterSeq, membersCount}
      messages: [],             // хронология для рендера (стор + аутбокс)
      membersByPrincipal: {},   // 'employee:5' -> {displayName, isOwner, ...}
      namesByPrincipal: {},     // имена НАВСЕГДА, включая вышедших (§6)
      trustByPrincipal: {},     // доверие по КАЖДОЙ паре, а не одно на комнату
      // Участники, которые ещё не открыли чат: устройства нет, копию везти
      // некуда, и написанного сейчас они не увидят никогда
      membersWithoutDevices: [],
      // Запрет отправки (решение владельца 07.09): у кого-то из ПОЛУЧАТЕЛЕЙ
      // сменился ключ, и человек ещё не решил — сверить или отправить всё
      // равно. null | [{principalKey, name}]; считается по текущему составу
      // доставки, а не по всей карте доверия (в ней живут и вышедшие).
      sendBlocked: null,
      // ATT С3: ход загрузки файла ({name, size, percent}) — грузится ВНЕ
      // очереди машины, как в 1:1
      uploading: null,
      self: null,               // {principalType, principalId, key}
    };
  }

  _emit(patch) {
    // После stop() страница уже размонтирована: хвост async-работы не должен
    // трогать UI (урок QR-1b р.2, High)
    if (this.stopped) return;
    this.state = { ...this.state, ...patch };
    this.onUpdate(this.state);
  }

  _checkStopped() {
    if (this.stopped) throw new StoppedError('RoomController');
  }

  _enqueue(fn) {
    return this.queue.enqueue(fn);
  }

  get selfKey() {
    return principalKey(this.principal.type, this.principal.id);
  }

  // ------------------------------------------------------------- старт

  async start() {
    try {
      await this._enqueue(() => this._bootstrap());
      if (this.stopped || this.state.phase === 'error') return;
      this._emit({ phase: 'ready' });
      this._startStream(); // фоновый цикл, не await
    } catch (e) {
      if (e instanceof StoppedError || this.stopped) return;
      this._emit({ phase: 'error', error: roomBootstrapError(e) });
    }
  }

  /**
   * Живая лента: подписка на СВОЙ канал устройства.
   *
   * 🔴 Канал у устройства ОДИН на всё: сервер шлёт в него и уведомления
   * переписки 1:1 (`conversation_id`), и комнатные (`room_id`). Чужие кадры
   * молча пропускаем — сравнение по `room_id` это и делает, а обратное
   * (догон комнаты по любому кадру) жгло бы запросы на каждое личное
   * сообщение владельца.
   *
   * Поднимается ПОСЛЕ bootstrap: до него нет ни комнаты, ни машины, и
   * догонять пришедшее было бы нечем.
   */
  _startStream() {
    this.stream = startStreamLoop({
      api: this.api,
      isStopped: () => this.stopped,
      onNotify: (n) => {
        if (this.room && n?.room_id === this.room.id) this.requestCatchUp();
      },
      onReady: () => this._flushOnReady(),
      // 403 в потоке — про УСТРОЙСТВО (ревокация), а не про сеть: цикл встал
      // навсегда, и вкладка обязана сказать это словами, а не крутить приём.
      onFatal: (e) => this._emit({ phase: 'error', error: roomBootstrapError(e) }),
    });
  }

  /**
   * Связь вернулась (подписка встала): сначала ДОСЛАТЬ зависший аутбокс, потом
   * догон — иначе своё сообщение встало бы в ленте после чужих, пришедших
   * позже. Порядок держится очередью: досыл встаёт в неё раньше догона.
   *
   * Без этого сообщение, написанное без связи, не терялось (конверт лежит в
   * аутбоксе с тем же `message_id`), но и не улетало само: только по кнопке
   * «повторить» или при следующем открытии чата. Человек при этом видит
   * «отправляется…» и не знает, что от него ждут действия.
   */
  _flushOnReady() {
    this._enqueue(async () => {
      try {
        // История перечитывается, ТОЛЬКО если что-то реально ушло: обычный
        // реконнект — это пустая очередь, а неудачный досыл ничего в сторе не
        // меняет. Догон и так кончается _loadHistory.
        if (await this._resendOutbox()) await this._loadHistory();
      } finally {
        // 🔴 Догон ставится ОТСЮДА, а не рядом с этим шагом (ревью Codex р.1,
        // Medium). Снаружи `requestCatchUp()` схлопнулся бы в догон, который
        // УЖЕ стоит в очереди с прошлого уведомления, — а тот стоит ВПЕРЕДИ
        // досыла, и обещанный порядок «сначала досыл» превращался бы в свою
        // противоположность ровно тогда, когда он и нужен: связь рвалась,
        // конверт завис, уведомление успело прийти до обрыва.
        //
        // Изнутри шага этой развилки нет: очередь последовательна, значит
        // прежний догон уже отработал и флаг снят. `finally` — потому что
        // упавший досыл не повод не забирать чужие сообщения.
        this.requestCatchUp();
      }
    }).catch(() => { /* причину уже показал _deliver, либо отказ стора */ });
    // 🔴 .catch выше не избыточен: очередь гасит ошибку только в СВОЕЙ
    // цепочке, а возвращает промис шага — он реджектится и без обработчика
    // даёт unhandledrejection.
  }

  async _bootstrap() {
    const { TotalMsgClient } = await this.loadCrypto();
    this._checkStopped();
    this.store = await this.storeFactory().open();
    this._checkStopped();
    this.client = new TotalMsgClient(this.api, {
      principalType: this.principal.type,
      principalId: this.principal.id,
      did: this.did,
      allowTakeover: this.allowTakeover,
    });
    // 🔴 Имя стора и passphrase — ТЕ ЖЕ, что у 1:1: машина одна на
    // (принципал, устройство), и второй стор означал бы второе устройство со
    // сменой ключа у всех собеседников разом (§8 дизайна).
    await this.client.init({
      storeName: storeNameFor(this.principal, this.did),
      passphrase: storePassphrase(),
    });
    this._checkStopped();
    await this.client.publishKeys();
    this._checkStopped();
    await this._topUpPrekeys();
    this._checkStopped();
    this._emit({
      self: {
        principalType: this.principal.type,
        principalId: this.principal.id,
        key: this.selfKey,
      },
    });
    await this._resolveRoom();
    this._checkStopped();
    await this._syncRoster();
    this._checkStopped();
    await this._syncDevices();
    this._checkStopped();
    await this._loadHistory();
    await this._resendOutbox();
    this._checkStopped();
    // Ошибка ПЕРВОГО догона — не «крипта не поднялась»: машина жива, комната
    // на месте, копии ждут на сервере. Честная плашка и повтор.
    await this._catchUp().catch((e) => this._catchUpFailed(e));
  }

  /**
   * Долив своего пула одноразовых ключей — при КАЖДОМ открытии чата.
   *
   * 🔴 Без него комната встаёт целиком. Ключи из пула жгут те, кто заводит с
   * нашим устройством Olm-сессию; пустой пул превращает нас в получателя, для
   * которого копию построить нельзя, а «пачка целиком или никак» отбивает
   * тогда ВСЮ отправку — у всех и у каждого (§4.1 дизайна). Сервер это
   * состояние сам не лечит: снимается оно доливом у того, кто заблокировал.
   *
   * 🔴 Отказ долива подъём НЕ рушит. Читать комнату можно и с пустым пулом, а
   * настоящую беду покажут следующие шаги: они ходят тем же транспортом и в
   * тот же сервер, и говорят о ней своими словами. Свои у долива были бы
   * заведомо неверными — 404 здесь значит «нет устройства», а не «нет
   * комнаты», и подставлять человеку вторую формулировку нельзя.
   */
  async _topUpPrekeys() {
    try {
      await this.client.topUpPrekeys();
    } catch {
      /* см. выше: диагноз ставят следующие шаги подъёма */
    }
  }

  /**
   * Комнату ЗАВОДИТ только владелец (участнику там 403) — участник видит свою
   * через список. Пустой список у участника значит, что владелец общий чат ещё
   * не открывал.
   */
  async _resolveRoom() {
    const raw = this.principal.type === 'owner'
      ? await this.api.ensureRoom()
      : (await this.api.listRooms())?.rooms?.[0] ?? null;
    if (!raw) throw new NoRoomError();
    this.room = {
      id: raw.id,
      title: raw.title,
      lastSeq: raw.last_seq ?? 0,
      joinedAfterSeq: raw.joined_after_seq ?? 0,
      membersCount: raw.members_count ?? 0,
    };
    this._emit({ room: this.room });
  }

  /**
   * Переименование комнаты владельцем: имя разговора и/или СВОЯ подпись в нём.
   *
   * Зачем: и то и другое родилось из подписи приглашения, где владелец пишет
   * «кому», — на бою комната так и назвалась именами двух коллег.
   *
   * 🔴 Ошибка НЕ гасится и НЕ уходит в общую плашку чата: переименование
   * затевает человек и прямо сейчас ждёт ответа, поэтому промис реджектится, а
   * показывает отказ форма, рядом с полями. Общая плашка («сообщение не
   * ушло») тут соврала бы дважды: и про то, что случилось, и про то, что
   * делать.
   *
   * 🔴 Ростер перечитывается ПОСЛЕ смены подписи: имя владельца живёт в нём, и
   * без чтения в списке участников и над сообщениями осталось бы старое.
   */
  rename({ title, displayName } = {}) {
    return this._enqueue(async () => {
      if (!this.room) return;
      const body = {};
      if (title !== undefined) body.title = title;
      if (displayName !== undefined) body.display_name = displayName;
      const raw = await this.api.renameRoom(this.room.id, body);
      this.room = { ...this.room, title: raw?.title ?? this.room.title };
      this._emit({ room: this.room });
      if (body.display_name !== undefined) await this._syncRoster();
    });
  }

  // ------------------------------------------------------------- ростер

  /**
   * Ростер комнаты. Имена копятся НАВСЕГДА и отдельно от ростера: вышедшего
   * участника в `/members` уже нет, а его сообщения в истории остались, и без
   * запомненного имени история после первого же выхода показывала бы пустоту
   * (§6 дизайна).
   */
  async _syncRoster() {
    const { members = [] } = await this.api.roomMembers(this.room.id);
    const byPrincipal = {};
    for (const m of members) {
      byPrincipal[principalKey(m.principal_type, m.principal_id)] = {
        principalType: m.principal_type,
        principalId: m.principal_id,
        displayName: m.display_name,
        joinedAt: m.joined_at,
        isOwner: m.is_owner,
      };
    }
    const namesKey = `room-names:${this.room.id}`;
    const known = (await this.store.getKV(namesKey)) ?? {};
    const names = { ...known };
    let changed = false;
    for (const [key, m] of Object.entries(byPrincipal)) {
      if (names[key] !== m.displayName) {
        names[key] = m.displayName;
        changed = true;
      }
    }
    if (changed) await this.store.setKV(namesKey, names);
    this._names = names;
    this._emitMaps({ membersByPrincipal: byPrincipal, namesByPrincipal: names });
    return byPrincipal;
  }

  /**
   * Устройства-получатели: они же обязательный состав fan-out, они же повод
   * пересчитать доверие. Ручка identity-бандлов one-time ключей НЕ жжёт —
   * поэтому перечитывать её перед каждой отправкой честно и дёшево.
   *
   * 🔴 Доверие пиняется по КАЖДОЙ паре (тип, id) отдельно: смена ключа одного
   * участника не имеет права трогать остальных.
   */
  /**
   * МУ-1-1: доверие к человеку с НЕСКОЛЬКИМИ устройствами.
   *
   * 🔴 Беда, ради которой срез. Пин хранился по человеку, а цикл шёл по
   * устройствам: два устройства одного человека писали в один пин, второе
   * выглядело сменой ключа, состояние липкое (решение 07.09) — и отправка
   * блокировалась навсегда.
   *
   * 🔴 Почему два пути, а не один «по устройству» (ревью МУ-1-1, раунды 1-4).
   * Пока у человека одно устройство — а сегодня так у всех, на бэкенде стоит
   * `uq_msg_devices_active`, — любое доверие «по устройству» оказывается ТИШЕ
   * боя: бой сравнивает с последним увиденным устройством, а память по
   * устройствам молча узнаёт вернувшееся старое (телефон, снова забравший чат,
   * в том числе потерянный). Четыре раунда подряд ревью находило новый вход
   * такого рода — это противоречие постановки, а не очередной баг. Поэтому:
   * - ОДНО устройство с ключом — дословно код боя: тот же ключ хранилища, тот
   *   же формат, та же запись. Обрывы, откаты, переустановка, возврат
   *   устройства ведут себя как на бою по построению;
   * - НЕСКОЛЬКО устройств — запись по устройствам, одной записью на человека.
   *   На бою этот путь пока недостижим; его смысл (что считать своим
   *   устройством, а что подменой) решается вместе с привязкой в МУ-1-4.
   */
  _identityKey(key) { return `room-identity-devices:${this.room.id}:${key}`; }

  /** Пин по человеку — форма боя и путь одного устройства. */
  _pinKeyLegacy(key) { return `room-identity:${this.room.id}:${key}`; }

  /** Одно устройство: код боя без изменений. */
  async _judgeSingle(key, e, ed) {
    const pinKey = this._pinKeyLegacy(key);
    const known = await this.store.getKV(pinKey);
    const verdict = tofuVerdict(known, ed);
    if (verdict.repin) {
      // 🔴 При смене ключа `changedAt` делает состояние ЛИПКИМ (решение
      // 07.09): без него следующий же пересмотр состава — а он идёт перед
      // каждой отправкой — смыл бы «ключ СМЕНИЛСЯ» в «без изменений», и
      // запрет отправки снимался бы сам, без человека. Прежние `verifiedAt`
      // и `ackedAt` в новый пин не переезжают: оба были сказаны про
      // ПРЕЖНИЙ ключ.
      await this.store.setKV(pinKey, {
        ed25519: ed, did: e.did, deviceRowId: e.deviceId, verifiedAt: null,
        ...(verdict.changed ? { changedAt: Date.now() } : {}),
      });
    } else if (known.deviceRowId !== e.deviceId || known.did !== e.did) {
      await this.store.setKV(pinKey, {
        ...known, did: e.did, deviceRowId: e.deviceId,
      });
    }
    if (verdict.changed) await this._writeSystemRow('key_changed', e);
    return { known, verdict };
  }

  /**
   * Несколько устройств: приговор одному из них. Меняет `pins` на месте;
   * запись — у вызывающего, ОДНА на человека (две записи — пин и список —
   * открывали щель, в которой обрыв терял тревогу, ревью р.1-3).
   *
   * Пин прежней формы переносится, только пока записи по человеку ещё нет и
   * только на своё устройство (`did` совпал). «Человек знаком» — по прежнему
   * пину или по другому устройству в записи: новое устройство знакомого
   * человека — тревога, как и смена ключа.
   */
  _judgeDevice(pins, e, ed, { hadRecord, legacy }) {
    let known = pins[e.did] ?? null;
    if (!known && !hadRecord && legacy && legacy.did === e.did) {
      known = legacy;
      pins[e.did] = legacy;
    }
    const personKnown = Boolean(legacy)
      || Object.keys(pins).some((did) => did !== e.did);
    const verdict = !known && personKnown
      ? { status: 'key_changed', changed: true, repin: true }
      : tofuVerdict(known, ed);
    if (verdict.repin) {
      pins[e.did] = {
        ed25519: ed, did: e.did, deviceRowId: e.deviceId, verifiedAt: null,
        ...(verdict.changed ? { changedAt: Date.now() } : {}),
      };
    } else if (known.deviceRowId !== e.deviceId || known.did !== e.did) {
      pins[e.did] = { ...known, did: e.did, deviceRowId: e.deviceId };
    }
    return { known, verdict };
  }

  async _judgeMany(key, judged) {
    const recordKey = this._identityKey(key);
    const record = (await this.store.getKV(recordKey)) ?? null;
    const legacy = (await this.store.getKV(this._pinKeyLegacy(key))) ?? null;
    const pins = { ...(record?.pins ?? {}) };
    const before = JSON.stringify(pins);
    const out = judged.map(({ e, ed }) => ({
      e, ed, ...this._judgeDevice(pins, e, ed, { hadRecord: Boolean(record), legacy }),
    }));
    // 🔴 ОДНА запись на человека: приговоры всех его устройств ложатся разом
    // или не ложатся вовсе. Системная строка — после записи, как и на бою.
    if (JSON.stringify(pins) !== before) {
      await this.store.setKV(recordKey, { ...(record ?? {}), pins });
    }
    for (const { e, verdict } of out) {
      if (verdict.changed) await this._writeSystemRow('key_changed', e);
    }
    return out;
  }

  async _syncDevices() {
    const { devices = [] } = await this.api.roomDevices(this.room.id);
    const entries = toDeviceEntries(devices);
    // 🔴 Сначала — СКОРМИТЬ бандлы машине. Без этого шага машина не знает
    // получателей вовсе: сессий «не хватает» ей не для кого, а шифровать она
    // отказывается словами «не вижу устройство». Ключей это не жжёт — в ответе
    // сервера только identity, one-time идут отдельной ручкой claim.
    await this.client.learnDevices(entries);
    const trust = { ...this.state.trustByPrincipal };
    // 🔴 МУ-1-1: считаем ПО УСТРОЙСТВАМ, наружу отдаём сводкой по человеку.
    // Раньше запись велась прямо в `trust[key]`, и второе устройство того же
    // человека затирало первое — а вместе с этим выглядело сменой ключа.
    const byPrincipal = new Map();
    for (const e of entries) {
      const key = principalKey(e.principalType, e.principalId);
      if (!byPrincipal.has(key)) byPrincipal.set(key, []);
      byPrincipal.get(key).push(e);
    }
    for (const [key, own] of byPrincipal) {
      const devs = [];
      const judged = [];
      for (const e of own) {
        try {
          judged.push({ e, ed: ed25519Of(e.deviceKeys, e.did) });
        } catch {
          // Бандл без ed25519 — кривые данные сервера. Копию этому устройству
          // всё равно везти обязаны (оно в обязательном составе), но и врать
          // про доверие нельзя: показываем «неизвестно», а не «всё хорошо».
          devs.push({
            status: 'unknown',
            verified: false,
            acked: false,
            ed25519: null,
            did: e.did,
            deviceRowId: e.deviceId,
          });
        }
      }
      const judgedOut = judged.length > 1
        ? await this._judgeMany(key, judged)
        : await Promise.all(judged.map(async ({ e, ed }) => ({
          e, ed, ...(await this._judgeSingle(key, e, ed)),
        })));
      for (const { e, ed, known, verdict } of judgedOut) {
        devs.push({
          status: verdict.status,
          verified: verdict.status === 'verified',
          // Свежая смена сбрасывает прежний ack вместе с пином; липкое
          // состояние несёт ack из пина — человек уже решал про ЭТОТ ключ.
          acked: !verdict.changed && Boolean(known?.ackedAt),
          // Ключ — В СОСТОЯНИИ (ревью WEB-14 р.2, High): «Код сверен» и
          // «Отправить всё равно» обязаны привязываться к ключу, про который
          // человек видел предупреждение, а не к тому, что успел перепиниться,
          // пока клик стоял в очереди.
          ed25519: ed,
          did: e.did,
          deviceRowId: e.deviceId,
        });
      }
      // Сводка пессимистичная: одно сменившее ключ устройство держит человека
      // подозрительным, и числа для сверки берутся именно у него.
      const summary = trustSummary(devs);
      if (summary) trust[key] = summary;
    }
    this._devices = entries;
    // 🔴 Участник, который ещё не открыл общий чат, устройства не имеет — и
    // сообщения, отправленные до этого момента, он не увидит НИКОГДА (правило
    // «история начинается с момента, когда открыл чат»). Сервер такую пачку
    // примет молча, поэтому предупредить обязан клиент (ревью В1 р.1 проход Б,
    // High): иначе владелец уверен, что написал коллеге, а коллега не получил
    // ничего.
    const withDevices = new Set(entries.map(
      (e) => principalKey(e.principalType, e.principalId)));
    const silent = Object.entries(this.state.membersByPrincipal)
      .filter(([key]) => key !== this.selfKey && !withDevices.has(key))
      .map(([key, m]) => ({ principalKey: key, name: m.displayName }));
    // 🔴 Запрет считается по ТЕКУЩЕМУ составу доставки (withDevices), тем же
    // проходом, что и доверие, — а не по всей карте: в карте остаются и
    // вышедшие, их старый «ключ сменился» не должен останавливать комнату,
    // которой они больше не получатели.
    const blocked = [...withDevices]
      .filter((key) => trustBlocksSend(trust[key]))
      .map((key) => ({
        principalKey: key,
        name: this.state.membersByPrincipal[key]?.displayName
          ?? this._names[key] ?? 'участник',
      }));
    this._emitMaps({
      trustByPrincipal: trust,
      membersWithoutDevices: silent,
      sendBlocked: blocked.length ? blocked : null,
    });
    return entries;
  }

  /**
   * Системная строка в хронологии («ключ участника сменился»). Молчание тут
   * недопустимо: смена ключа — единственное видимое человеку событие, которое
   * отличает переустановку приложения от подмены.
   *
   * Ключ строки включает устройство — повторные подъёмы контроллера
   * перезаписывают ТУ ЖЕ строку, а не плодят её копии.
   */
  async _writeSystemRow(kind, entry) {
    await this.store.putRoomMessage({
      roomId: this.room.id,
      messageId: `sys:${kind}:${entry.principalType}:${entry.principalId}`
        + `:${entry.deviceId}`,
      roomSeq: this._seenSeq,
      direction: 'system',
      kind,
      text: null,
      sentAt: Date.now(),
      senderType: entry.principalType,
      senderId: entry.principalId,
      senderDeviceId: entry.deviceId,
    });
  }

  /**
   * Записать решение человека (`verifiedAt` или `ackedAt`) в пины устройств
   * из снимка — ОДНОЙ записью, как и приговоры. Устройство, чей пин успел
   * смениться на другой ключ, решения не получает: оно было сказано про
   * прежний. Возвращает did тех, кому решение записано.
   *
   * Путь выбирается тем же правилом, что и в _syncDevices: одно устройство с
   * ключом — пин по человеку, дословно как на бою.
   */
  async _decide(key, snapshot, field) {
    const now = Date.now();
    if (snapshot.length === 1) {
      const [d] = snapshot;
      const pinKey = this._pinKeyLegacy(key);
      const known = await this.store.getKV(pinKey);
      if (!known || !d.ed25519 || known.ed25519 !== d.ed25519) return [];
      await this.store.setKV(pinKey, { ...known, [field]: now });
      return [d.did];
    }
    const recordKey = this._identityKey(key);
    const record = await this.store.getKV(recordKey);
    const pins = { ...(record?.pins ?? {}) };
    const done = [];
    for (const d of snapshot) {
      const known = pins[d.did];
      if (!known || !d.ed25519 || known.ed25519 !== d.ed25519) continue;
      pins[d.did] = { ...known, [field]: now };
      done.push(d.did);
    }
    if (done.length) await this.store.setKV(recordKey, { ...record, pins });
    return done;
  }

  /** Владелец сверил числа голосом и подтвердил ключ КОНКРЕТНОГО участника.
   *  Сверка снимает и запрет отправки ПО ЭТОМУ участнику; если он был
   *  последним из остановивших, зависшее в аутбоксе досылается само.
   *
   *  🔴 Ревью WEB-14 р.2, High: сверка привязана к КЛЮЧУ, про который человек
   *  видел плашку, — снимок берётся в момент клика. Если, пока клик стоял в
   *  очереди, участник перепинился на другой ключ, сверка не применяется:
   *  на экране к этому моменту уже свежая тревога, решать надо заново. */
  markVerified(principalType, principalId) {
    const key = principalKey(principalType, principalId);
    const entry = this.state.trustByPrincipal[key];
    // 🔴 МУ-1-1: сверяем ВСЕ устройства человека, каждое в свой пин. Снимок
    // ключей — в момент клика, как и было: устройство, успевшее перепиниться,
    // пока клик стоял в очереди, сверенным не считается.
    const snapshot = (entry?.devices ?? [{ did: entry?.did, ed25519: entry?.ed25519 }])
      .filter((d) => d && d.did && d.ed25519)
      .map((d) => ({ did: d.did, ed25519: d.ed25519 }));
    return this._enqueue(async () => {
      if (!snapshot.length) return;
      // 🔴 Гонка с перепином (ревью WEB-14 р.2, High), теперь по устройствам:
      // пока клик стоял в очереди, состав устройств человека мог измениться —
      // например, появилось новое устройство или сменился ключ. Тогда сверка
      // НЕ применяется: на экране уже свежая тревога, решать надо заново.
      if (!sameKeyedDevices(snapshot, this.state.trustByPrincipal[key]?.devices)) return;
      const done = await this._decide(key, snapshot, 'verifiedAt');
      if (!done.length) return;
      const devs = (entry?.devices ?? []).map((d) => (done.includes(d.did)
        ? { ...d, status: 'verified', verified: true } : d));
      const summary = devs.length ? trustSummary(devs) : null;
      // 🔴 Снимаем запрет по этому человеку только если сводка его больше не
      // держит: у него могло остаться НЕсверенное устройство.
      const stillBlocks = summary ? trustBlocksSend(summary) : false;
      const rest = (this.state.sendBlocked ?? [])
        .filter((b) => (b.principalKey === key ? stillBlocks : true));
      const unblocked = this.state.sendBlocked && !rest.length;
      // через _emitMaps, а не _emit: строки истории несут доверие в себе, и
      // снимок, где карта уже «подтверждено», а строки ещё нет, — тот же
      // рассогласованный снимок (ревью р.2, Medium)
      this._emitMaps({
        trustByPrincipal: {
          ...this.state.trustByPrincipal,
          [key]: summary ?? {
            ...this.state.trustByPrincipal[key], status: 'verified', verified: true,
          },
        },
        sendBlocked: rest.length ? rest : null,
      });
      if (unblocked) {
        if (await this._resendOutbox()) await this._loadHistory();
      }
    });
  }

  /**
   * «Отправить всё равно» — решение владельца 07.09: смена ключа СТАВИТ
   * отправку, но человек вправе осознанно продолжить. Подтверждаются ВСЕ
   * остановившие получатели разом — кнопка одна на пачку, копии в комнате
   * едут «целиком или никак». Решение пишется в пины (`ackedAt`) и потому
   * переживает перезагрузку; тревога НЕ снимается — плашки остаются красными
   * до настоящей сверки, а НОВАЯ смена ключа перепинит запись и снова
   * остановит отправку.
   */
  sendAnyway() {
    // 🔴 Ревью WEB-14 р.2, High: снимок — В МОМЕНТ КЛИКА. Подтверждаются
    // ровно те ключи, про которые человек видел предупреждение; участник,
    // успевший перепиниться, пока клик стоял в очереди, остаётся под
    // запретом — на экране уже свежая тревога, решать надо заново.
    const snapshot = (this.state.sendBlocked ?? []).map((b) => ({
      ...b,
      ed25519: this.state.trustByPrincipal[b.principalKey]?.ed25519 ?? null,
      // МУ-1-1: снимок несёт и состав устройств — по нему в очереди сверяем,
      // что человек не перепинился, пока клик ждал.
      devices: (this.state.trustByPrincipal[b.principalKey]?.devices ?? [])
        .map((d) => ({ did: d.did, ed25519: d.ed25519 })),
    }));
    return this._enqueue(() => this._guard(async () => {
      if (!this.room || !snapshot.length) return;
      const trust = { ...this.state.trustByPrincipal };
      for (const b of snapshot) {
        const entry = trust[b.principalKey];
        // 🔴 Та же гонка: состав устройств изменился — подтверждение про
        // прежний состав не применяем.
        if (!sameKeyedDevices(b.devices, entry?.devices)) continue;
        // 🔴 МУ-1-1: подтверждаем КАЖДОЕ устройство человека в свой пин.
        // Подтверждение одного не имеет права говорить за другое — иначе
        // связанные устройства станут способом протащить подмену.
        const devs = (entry?.devices ?? [{ did: entry?.did, ed25519: entry?.ed25519 }])
          .filter((d) => d && d.did && d.ed25519);
        // eslint-disable-next-line no-await-in-loop
        const acked = await this._decide(b.principalKey, devs, 'ackedAt');
        if (!acked.length || !entry) continue;
        const next = (entry.devices ?? []).map((d) => (acked.includes(d.did)
          ? { ...d, acked: true } : d));
        trust[b.principalKey] = next.length
          ? trustSummary(next)
          : { ...entry, acked: true };
      }
      // Остаток запрета — по тому, что РЕАЛЬНО подтверждено: непризнанные
      // (перепинившиеся) остаются в строке, досыл идёт только при пустом
      // остатке — пачка комнаты едет целиком или никак.
      const rest = (this.state.sendBlocked ?? [])
        .filter((b) => trustBlocksSend(trust[b.principalKey]));
      this._emitMaps({ trustByPrincipal: trust, sendBlocked: rest.length ? rest : null });
      if (!rest.length) {
        if (await this._resendOutbox()) await this._loadHistory();
      }
    }, 'Не удалось подтвердить отправку — повторите'));
  }

  // ------------------------------------------------------------- история

  async _loadHistory() {
    const rows = await this.store.listRoomMessages(this.room.id);
    const outbox = await this.store.listRoomOutbox(this.room.id);
    // Строка, у которой уже есть живая замена, человеку не показывается: он
    // видел бы свой текст дважды, а уедет из этих двух ровно одна.
    // 🔴 Замена считается живой и когда она УЖЕ ДОЕХАЛА (ревью р.5, High):
    // иначе успешная отправка вместе с неудачной уборкой воскрешает старую
    // строку рядом с отправленной — и это переживает перезагрузку вкладки.
    // 🔴 Доставленным считается ТОЛЬКО СВОЁ отправленное (ревью р.7, High).
    // Возьми все строки истории — и входящее сообщение ЧУЖОГО отправителя с тем
    // же message_id (а это ровно случай коллизии, из-за которой замена и
    // затевается) спрячет и молча удалит наш собственный текст.
    const delivered = new Set(rows
      .filter((r) => r.direction === 'out')
      .map((r) => r.messageId));
    const ids = new Set([...outbox.map((e) => e.messageId), ...delivered]);
    const pending = outbox.filter((e) => (
      // 🔴 Строка, которая УЖЕ доехала, но осталась в аутбоксе из-за отказа
      // уборки, человеку не показывается: он видел бы свой текст дважды
      // (ревью р.6, High).
      !delivered.has(e.messageId)
      && !(e.replacedBy && ids.has(e.replacedBy))
    ));
    const messages = [
      ...rows.map((r) => this._decorate(r, { pending: false, state: 'sent' })),
      ...pending.map((p) => this._decorate({
        roomId: p.roomId,
        messageId: p.messageId,
        roomSeq: null,
        direction: 'out',
        text: p.text,
        sentAt: p.sentAt,
        senderType: this.principal.type,
        senderId: this.principal.id,
        senderDeviceId: null,
        attachments: p.attachments ?? null,
        replyTo: p.replyTo ?? null,
      }, {
        // FAILED_TERMINAL — это не «отправляется»: человеку нужна кнопка,
        // а не вечные часики
        pending: p.state !== 'failed_terminal',
        state: p.state ?? 'pending',
        attempts: p.attempts ?? 0,
        blocked: p.blocked ?? [],
      })),
    ];
    this._emit({ messages });
  }

  /**
   * Строка для экрана: у КАЖДОЙ есть отправитель (в комнате он не один) и имя,
   * которое не пропадает вместе с человеком.
   */
  _decorate(row, extra, maps = null) {
    const members = maps?.membersByPrincipal ?? this.state.membersByPrincipal;
    const names = maps?.namesByPrincipal ?? this.state.namesByPrincipal;
    const trust = maps?.trustByPrincipal ?? this.state.trustByPrincipal;
    const key = principalKey(row.senderType, row.senderId);
    const member = members[key];
    return {
      ...row,
      ...extra,
      principalKey: key,
      senderIsSelf: key === this.selfKey,
      senderInRoster: Boolean(member),
      // 🔴 Запасной вариант обязателен: отправитель мог выйти или быть удалён,
      // а его сообщения в истории остались
      senderLabel: member?.displayName ?? names[key] ?? 'Участник вне комнаты',
      trust: trust[key] ?? null,
    };
  }

  /**
   * Отдать НАРУЖУ карты вместе с пересобранными под них строками.
   *
   * 🔴 Иначе снимок уходит рассогласованным: карта уже говорит «ключ
   * сменился», а строки в том же снимке несут прежнее доверие и прежнее имя
   * (ревью В1 р.1 проход Б, Medium). Экран читает и то и другое, и решать,
   * какой половине верить, он не обязан.
   */
  _emitMaps(patch) {
    // Единственная дверь, через которую меняется sendBlocked (пересмотр
    // состава, «Код сверен», «Отправить всё равно»). Запрет снят — плашка
    // отказа «не отправлено: сменился ключ» потеряла причину и гаснет здесь
    // же (ревью С3 р.3, Medium); доменные и терминальные ошибки не трогаем.
    const lifted = 'sendBlocked' in patch && !patch.sendBlocked
      && this.state.error?.kind === 'send_refused';
    this._emit({
      ...patch,
      ...(lifted ? { error: null } : {}),
      messages: this.state.messages.map((m) => this._decorate(m, {}, {
        ...this.state, ...patch,
      })),
    });
  }

  // ------------------------------------------------------------- отправка

  /**
   * Отправить текст в комнату, с вложениями или без. Ошибки не бросает —
   * несёт их через state.
   *
   * 🔴 Контракт — тот же, что у 1:1 после C2a (это и есть закрытие WEB-20):
   * ВСЕГДА разрешается в boolean. `false` = «ничего не ушло, верните
   * черновик» (страница очистила поле до вызова); `true` = сообщение
   * ДОЛГОВЕЧНО (лежит в аутбоксе и доедет само или по «повторить») — даже
   * если доставка прямо сейчас споткнулась. До этой правки успех и провал
   * одинаково давали `undefined`, и черновик терялся при остановленном
   * контроллере (переключение вкладки участником — ровно случай C2a).
   */
  send(text, files = [], replyTo = null) {
    if (!files?.length) {
      return this._enqueueSend(() => this._sendStep(text, null, replyTo));
    }
    return this._sendWithFiles(text, files, replyTo);
  }

  /** Приведение к контракту: `undefined` (остановленная очередь, глотание
   *  _guard) и реджект — это `false`, а не «может быть». Зеркало C2a. */
  _enqueueSend(fn) {
    return Promise.resolve(this._enqueue(fn))
      .then((sent) => (sent === undefined ? false : sent))
      .catch(() => {
        this._emit({
          error: { text: 'Сообщение не ушло — попробуйте ещё раз', retriable: false },
        });
        return false;
      });
  }

  /**
   * Шифрование и загрузка файлов — ВНЕ очереди машины (как в 1:1): 25 МБ по
   * мобильной сети — минуты, и держать ими догон с приёмом нельзя.
   */
  async _sendWithFiles(text, files, replyTo = null) {
    // Пред-проверка запрета ДО шифрования и загрузки — не гнать 25 МБ, пока
    // человек не решил про сменившийся ключ. Текст без файлов при запрете
    // ЖДЁТ в аутбоксе (решение с.82 — «отправить всё равно» досылает), но
    // файлу ждать опасно: нерешённый запрет длиннее суток скормил бы блоб
    // свипу сирот, и досыл честно упал бы «файла больше нет». Черновик
    // возвращает страница.
    if (this.state.sendBlocked) {
      this._emitSendBlockedRefusal();
      return false;
    }
    const metas = [];
    try {
      for (const file of files) {
        this._emit({ uploading: { name: file.name || 'файл', size: file.size, percent: null } });
        const { ciphertext, meta } = await encryptFile(file);
        const ack = await this.api.uploadAttachment(ciphertext, {
          onUploadProgress: (ev) => {
            if (!ev?.total) return;
            this._emit({
              uploading: {
                name: meta.name, size: meta.size,
                percent: Math.min(100, Math.round((ev.loaded / ev.total) * 100)),
              },
            });
          },
        });
        metas.push(attachmentMeta({ attachmentId: ack.attachment_id, ...meta }));
      }
    } catch (e) {
      this._emit({ uploading: null, error: uploadError(e) });
      return false;
    }
    this._emit({ uploading: null });
    // Пока файл грузился, контроллер могли остановить — контракт тот же
    return this._enqueueSend(() => this._sendStep(text, metas, replyTo));
  }

  _emitSendBlockedRefusal() {
    this._emit({
      error: {
        text: 'Не отправлено: у кого-то в общем чате сменился ключ устройства. '
          + 'Сверьте код или подтвердите отправку — и отправьте заново.',
        retriable: false,
        // 🔴 Плашка живёт ровно столько, сколько живёт запрет (ревью С3 р.3,
        // Medium): снятие запрета гасит её в _emitMaps. Иначе после «Код
        // сверен» и удачной повторной отправки рядом с ушедшей строкой
        // оставалось бы красное «не отправлено» — и человек слал бы дубль.
        kind: 'send_refused',
      },
    });
  }

  /** Шаг очереди: аутбокс -> доставка. attachments уже с id. */
  async _sendStep(text, attachments, replyTo = null) {
    if (!this.room) return false;
    // 🔴 Пояс к запрету для ФАЙЛОВ (ревью С3 р.1, Medium — зеркало C2a):
    // запрет мог встать, ПОКА файл грузился вне очереди. Текст без файлов
    // при запрете честно ЖДЁТ в аутбоксе (решение с.82), но сообщение с
    // вложением в вечном pending скормило бы непривязанный блоб свипу сирот
    // (24 ч) — и досыл после «отправить всё равно» упал бы «файла больше
    // нет». Отказ ДО записи: false вернёт черновик странице.
    if (attachments?.length && this.state.sendBlocked) {
      this._emitSendBlockedRefusal();
      return false;
    }
    // Чистим СВОИМ контрактом, не доверяя странице (правило одно на оба конца).
    const quote = normalizeReplyTo(replyTo);
    const entry = {
      roomId: this.room.id,
      // 🔴 Рождается ЗДЕСЬ и переживает все повторы: сервер дедупит именно
      // по нему, а перезагрузка вкладки не должна плодить дубли
      messageId: newMessageId(),
      text,
      sentAt: Date.now(),
      attempts: 0,
      state: 'pending',
      copies: [],
      blocked: [],
      // Метаданные (с ключами) живут в аутбоксе: пересборка копий при
      // stale_roster шифрует их в конверты НОВЫХ устройств — без них файл
      // потерялся бы из сообщения молча. Блоб не перезаливается никогда.
      ...(attachments?.length ? { attachments } : {}),
      // WEB-16: цитата живёт в аутбоксе рядом с текстом: пересборка копий
      // после stale_roster шифрует её новым устройствам — без этого тот, кто
      // вошёл в комнату позже, получил бы то же сообщение БЕЗ цитаты.
      ...(quote ? { replyTo: quote } : {}),
    };
    // До этой строки любой отказ (квота стора, закрытая база) летит наружу —
    // _enqueueSend превратит его в `false`, страница вернёт черновик.
    await this.store.putRoomOutbox(entry); // текст на диске ДО первой попытки
    // 🔴 ГРАНИЦА durable (зеркало C2a р.7-р.8): с этой строки сообщение НЕ
    // ПОТЕРЯНО — его досылает _resendOutbox. Ошибки дальше — про ПОКАЗ или
    // про доставку «прямо сейчас», и отвечать на них false нельзя: страница
    // вернула бы черновик, человек отправил бы второй раз — и вышел бы дубль
    // с НОВЫМ message_id, которого серверный дедуп не видит.
    const noteDisplayGlitch = () => {
      if (this.state.error) return; // доменный отказ точнее и уже на экране
      this._emit({
        error: {
          text: 'Сообщение сохранено и будет отправлено, но экран не обновился — обновите страницу',
          retriable: true,
        },
      });
    };
    try { await this._loadHistory(); } catch { noteDisplayGlitch(); }
    let settled = false;
    try { settled = await this._deliver(entry); } catch { noteDisplayGlitch(); }
    // 🔴 Запрет, обнаруженный ВПЕРВЫЕ уже внутри _deliver (ревью С3 р.2,
    // Medium): свежий пересмотр состава принёс смену ключа ПОСЛЕ durable-
    // записи, но ДО того, как сервер принял пачку (принятая пачка — это
    // settled=true; 409 stale_roster приёмом не является). Текст в этой
    // точке честно ждёт решения человека (с.82), а сообщению с файлом ждать
    // нельзя — те же сутки свипа сирот. Посылка ОДНА на все три точки (до
    // загрузки, после загрузки, внутри доставки): файл под запретом НЕ
    // ЖДЁТ — запись отзывается, черновик (текст + файл) возвращается
    // странице; залитый блоб становится сиротой и уйдёт свипом, повторная
    // отправка зальёт его заново. «Ничего не ушло» здесь буквально верно —
    // контракт WEB-20 цел. Остановленный контроллер — не этот случай:
    // страницы, которой вернуть черновик, уже нет, запись ждёт подъёма.
    if (!settled && attachments?.length && this.state.sendBlocked
        && !this.stopped && entry.state === 'pending') {
      try {
        await this.store.deleteRoomOutbox(entry.messageId);
      } catch {
        // Отзыв не удался — запись остаётся pending, как у текста: досыл
        // после «Код сверен»/«Отправить всё равно» пройдёт здесь же.
        // Черновик НЕ возвращаем: с ним человек отправил бы дубль.
        try { await this._loadHistory(); } catch { noteDisplayGlitch(); }
        return true;
      }
      try {
        await this._loadHistory();
      } catch {
        // История не перечиталась — отозванную строку снимаем из снимка
        // сами (ревью С3 р.3, Low): иначе pending-часики стояли бы рядом с
        // возвращённым черновиком, а плашка отказа глушила бы noteDisplayGlitch.
        // Снимаем ТОЛЬКО свою исходящую: строка живёт под парой
        // (roomId, messageId), и чужое входящее с тем же id — не наша запись
        // (ревью С3 р.4, Low: та же дисциплина, что у сравнения по голому id).
        this._emit({
          messages: this.state.messages.filter(
            (m) => !(m.direction === 'out' && m.messageId === entry.messageId),
          ),
        });
      }
      this._emitSendBlockedRefusal();
      return false;
    }
    try { await this._loadHistory(); } catch { noteDisplayGlitch(); }
    return true;
  }

  /**
   * Один заход доставки: состав -> сессии -> копии -> пачка.
   * @returns {Promise<boolean>} сообщение РЕАЛЬНО settled (ушло или дубль).
   */
  async _deliver(entry) {
    if (!this.room) return false;
    for (;;) {
      if (this.stopped) return false;
      let entries;
      try {
        // 🔴 Ростер перечитывается ВМЕСТЕ с устройствами (ревью р.2, High):
        // «кто ещё не открыл чат» считается их разностью, и по устаревшей
        // карте она врёт в обе стороны — молчит про новичка и клевещет на
        // вышедшего. Ключей это чтение не жжёт.
        await this._syncRoster();
        entries = await this._syncDevices();
      } catch (e) {
        if (isFatal(e)) return this._terminalStop(entry, e);
        return this._hiccup();
      }

      // 🔴 Запрет отправки (решение 07.09) стоит ЗДЕСЬ — после свежего
      // пересмотра состава и ДО установления сессий и шифрования: ни одна
      // копия не имеет права быть зашифрована на непризнанный ключ. Конверт
      // остаётся в аутбоксе как pending — причина уже на экране
      // (sendBlocked эмитнул _syncDevices), после «Код сверен» или
      // «Отправить всё равно» досыл придёт сюда же и пройдёт.
      if (this.state.sendBlocked) return false;

      // 1. Сессии. claim — ОДНИМ запросом и только если есть кому: получателей
      //    не больше девяти (у принципала одно активное устройство), поэтому
      //    чанкования нет вовсе.
      let need = null;
      try {
        need = await this.client.missingSessions(entries);
      } catch (e) {
        if (isFatal(e)) return this._terminalStop(entry, e);
        return this._hiccup();
      }
      if (need && !need.deviceIds.length) {
        // Машина просит сессию с устройством, которого в обязательном составе
        // НЕТ (ревокнутое, уже выпавшее из ростера). Ключей для него мы не
        // просим, но и запрос обязаны закрыть честным «ключей не дали» —
        // иначе он остаётся висеть у машины навсегда.
        await this.client.establishSessions(need.request, [], entries);
      } else if (need) {
        let claimed;
        try {
          claimed = await this.api.claimRoomKeys(this.room.id, need.deviceIds);
        } catch (e) {
          if (isStaleRoster(e)) {
            if (await this._countStale(entry)) continue;
            return false;
          }
          if (isFatal(e)) return this._terminalStop(entry, e);
          const terminal = terminalSendError(e);
          if (terminal) return this._failTerminal(entry, terminal, {}, reasonOf(e));
          return this._hiccup();
        }
        const keys = claimed?.keys ?? [];
        const blocked = blockedDeviceIds(keys);
        await this.client.establishSessions(need.request, keys, entries);
        // 🔴 §4.1: получателя без ключей НЕ пропускаем молча и НЕ отправляем
        // вовсе. Неполная пачка вернулась бы 409 — то есть мы потратили бы
        // запрос на ответ, который знаем заранее, и запутали бы в логах
        // настоящую смену ростера с известной нехваткой ключей.
        if (blocked.length) return this._blockedKeys(entry, blocked, entries);
      }

      // 2. Копии: по одной на КАЖДОЕ обязательное устройство.
      let copies;
      try {
        copies = await this._buildCopies(entry, entries);
      } catch {
        return this._hiccup('Не удалось зашифровать сообщение — повторите');
      }
      entry.copies = copies;
      await this.store.putRoomOutbox(entry);

      // 3. Пачка — целиком или никак.
      try {
        const ack = await this.api.postRoomEnvelopes(this.room.id, {
          v: 1,
          message_id: entry.messageId,
          sent_at: entry.sentAt,
          // ATT С3: факт ссылки открыт (уборка + право скачивания), ключ — в
          // шифротексте копий. Поле опускается при пустоте: пачка без него
          // читается и старым сервером.
          ...(entry.attachments?.length
            ? { attachment_ids: entry.attachments.map((a) => a.id) } : {}),
          copies: copies.map((c) => ({
            recipient_device_id: c.recipientDeviceId, cipher: c.cipher,
          })),
        });
        await this._settleSent(entry, ack);
        return true;
      } catch (e) {
        if (isStaleRoster(e)) {
          if (await this._countStale(entry)) continue;
          return false;
        }
        // 🔴 ДО isFatal и ТОЛЬКО по машинному коду (ревью С3 р.1, High):
        // «блоб смётен свипом» терминален — шифротекста больше нет нигде.
        // Голый 404 в комнате занят другим приговором — «комнаты нет», и
        // спутать их значило бы съесть текст человека с ложным «прикрепите
        // заново», оставив комнату на вид живой. Статус-only эвристика 1:1
        // сюда не переносится сознательно.
        if (e?.status === 404 && e?.detail?.code === 'attachment_missing') {
          return this._failSweptAttachments(entry);
        }
        if (isFatal(e)) return this._terminalStop(entry, e);
        const terminal = terminalSendError(e);
        if (terminal) return this._failTerminal(entry, terminal, {}, reasonOf(e));
        return this._hiccup();
      }
    }
  }

  /**
   * Копии под текущий состав. Готовые шифротексты НЕ перешифровываются: при
   * повторе после `stale_roster` шифруются только НОВЫЕ устройства, а копии
   * исчезнувших просто не попадают в пачку.
   */
  async _buildCopies(entry, entries) {
    const ready = new Map((entry.copies ?? [])
      .filter((c) => c.cipher)
      .map((c) => [c.recipientDeviceId, c]));
    const copies = [];
    for (const e of entries) {
      const prev = ready.get(e.deviceId);
      // ATT С3: метаданные вложений — в шифротекст КАЖДОЙ копии; при
      // пересборке после stale_roster новые устройства получают те же
      // метаданные из аутбокса, блоб не перезаливается.
      const cipher = prev?.cipher
        ?? await this.client.encryptForDevice(
          e, entry.text, entry.sentAt, entry.attachments ?? null,
          entry.replyTo ?? null);
      copies.push({ recipientDeviceId: e.deviceId, cipher, state: 'ready' });
    }
    return copies;
  }

  /**
   * Сообщение принято сервером. `duplicate: true` — тоже принято: ack
   * потерялся, сервер дедупнул по `message_id` и вернул ПРЕЖНИЙ `room_seq`.
   */
  async _settleSent(entry, ack) {
    const seq = typeof ack?.room_seq === 'number' ? ack.room_seq : null;
    await this.store.putRoomMessage({
      roomId: entry.roomId,
      messageId: entry.messageId,
      roomSeq: seq,
      direction: 'out',
      text: entry.text,
      sentAt: entry.sentAt,
      senderType: this.principal.type,
      senderId: this.principal.id,
      senderDeviceId: this.client?.deviceRowId ?? null,
      attachments: entry.attachments ?? null,
      replyTo: entry.replyTo ?? null,
    });
    // 🔴 С этой строки доставка СОСТОЯЛАСЬ: сообщение принято сервером и
    // записано в историю. Всё, что ниже, — уборка, и её отказ не имеет права
    // превратить успех в «не ушло, повторите» (ревью р.5 и р.6, High).
    // Невычищенная строка аутбокса при этом не двоится и не досылается: и
    // показ, и досыл смотрят на историю (см. _loadHistory и _resendOutbox).
    try {
      // 🔴 Предшественников — ПЕРВЫМИ, и только потом саму строку (ревью р.8,
      // Medium). Обратный порядок: удалили себя, упали на уборке — и
      // предшественник остаётся НАВСЕГДА. Он скрыт (указывает на нас),
      // пропускается очередью (терминальный), а опоры для новой уборки больше
      // нет: доставленной в аутбоксе уже не лежит.
      await this._dropPredecessors(entry.messageId);
      await this.store.deleteRoomOutbox(entry.messageId);
    } catch {
      /* попробуем в следующий раз: ни видимость, ни доставка от этого не зависят */
    }
    // 🔴 Курсор догона по СВОЕМУ ack не двигаем: копий себе мы не шлём, а
    // чужие сообщения между нашими номерами — есть.
    if (seq !== null) this._seenSeq = Math.max(this._seenSeq, seq);
    if (this.state.error?.retriable) this._emit({ error: null });
  }

  /**
   * Убрать строки, чья замена уже доехала — и всю цепочку за ними.
   *
   * 🔴 Прятать предшественника «пока замена жива в аутбоксе» мало (ревью р.4,
   * High): замена уезжает и из аутбокса исчезает, а старая строка после этого
   * ВОСКРЕСАЕТ рядом с уже отправленным сообщением — и переживает
   * перезагрузку вкладки. Цепочка A -> B -> C убирается целиком.
   */
  async _dropPredecessors(messageId) {
    const outbox = await this.store.listRoomOutbox(this.room.id);
    const byTarget = new Map();
    for (const e of outbox) {
      if (!e.replacedBy) continue;
      if (!byTarget.has(e.replacedBy)) byTarget.set(e.replacedBy, []);
      byTarget.get(e.replacedBy).push(e.messageId);
    }
    // Слои цепочки: кто указывает на доставленную строку, кто — на них, и так
    // далее. `seen` — на случай кольца в испорченных данных: без него обход
    // не кончится вовсе.
    const seen = new Set([messageId]);
    const layers = [];
    let frontier = [messageId];
    while (frontier.length) {
      const next = [];
      for (const id of frontier) {
        for (const dead of byTarget.get(id) ?? []) {
          if (seen.has(dead)) continue;
          seen.add(dead);
          next.push(dead);
        }
      }
      if (next.length) layers.push(next);
      frontier = next;
    }
    // 🔴 Удаляем ДАЛЬНИЕ ПЕРВЫМИ (ревью р.6, Medium): если отказ случится на
    // дальнем, ближний ещё жив — и продолжает его скрывать. Обратный порядок
    // оставлял ссылку в пустоту, то есть воскресшую строку.
    //
    // Честно про покрытие: СЕГОДНЯ цепочка глубже одного слоя не собирается —
    // замена переводит предшественников на себя раньше, чем удаляет старую
    // строку. Мутант «убирать ближними первыми» поэтому эквивалентный, и
    // теста, который его убьёт, нет. Порядок оставлен сознательно: он стоит
    // пяти строк, а восстанавливать его придётся ровно в тот день, когда
    // появится путь, создающий вложенную цепочку.
    for (const layer of layers.reverse()) {
      for (const id of layer) await this.store.deleteRoomOutbox(id);
    }
  }

  /**
   * ATT С3: скачать и расшифровать вложение. Очередь машины не нужна — REST
   * и WebCrypto живут сами по себе (зеркало 1:1).
   */
  async downloadAttachment(meta) {
    const bytes = await this.api.downloadAttachment(meta.id);
    return decryptAttachment(bytes, meta);
  }

  /** 🔴 Блоб, не привязанный к сообщению сутки, сервер УБИРАЕТ — его 404 не
   *  лечится ни ретраем, ни временем: шифротекста больше нет нигде (ревью
   *  ATT С2 р.1, High — тот же приговор в комнате). Строка снимается;
   *  предшественники — ПЕРВЫМИ, иначе они воскресают (порядок _settleSent). */
  async _failSweptAttachments(entry) {
    try {
      await this._dropPredecessors(entry.messageId);
      await this.store.deleteRoomOutbox(entry.messageId);
    } catch {
      // Снять не вышло — хотя бы пометить терминальной: досыл такие не берёт.
      // Отказ и этой записи переживаем молча: приговор всё равно на экране.
      try {
        entry.state = 'failed_terminal';
        entry.terminalReason = 'attachment_swept';
        await this.store.putRoomOutbox(entry);
      } catch { /* стор лежит — следующий заход разберётся */ }
    }
    this._emit({
      error: {
        text: 'Сообщение не ушло: файл ждал отправки дольше суток, и сервер '
          + 'уже удалил его. Прикрепите файл заново.',
        retriable: false,
      },
    });
    return true; // строка снята (или терминальна) — очередь по ней не крутится
  }

  /** Ростер сменился. @returns можно ли пробовать ещё раз. */
  async _countStale(entry) {
    entry.attempts = (entry.attempts ?? 0) + 1;
    await this.store.putRoomOutbox(entry);
    if (entry.attempts <= MAX_STALE_RETRIES) return true;
    await this._failTerminal(entry,
      'Состав общего чата менялся быстрее, чем уходило сообщение. '
      + 'Текст сохранён — нажмите «повторить».', {}, 'stale_storm');
    return false;
  }

  /**
   * Терминальный отказ. 🔴 Сообщение ХРАНИТСЯ и ждёт ручного повтора:
   * исчерпание попыток не имеет права молча съесть текст человека.
   */
  async _failTerminal(entry, text, extra = {}, reason = 'unknown') {
    entry.state = 'failed_terminal';
    entry.terminalReason = reason;
    await this.store.putRoomOutbox(entry);
    this._emit({
      error: { text, retriable: false, messageId: entry.messageId, ...extra },
    });
    return false;
  }

  /** Получателю нечем зашифровать: пул one-time пуст и fallback не задан. */
  async _blockedKeys(entry, blockedIds, entries) {
    const byId = new Map(entries.map((e) => [e.deviceId, e]));
    const blocked = blockedIds.map((deviceId) => {
      const e = byId.get(deviceId);
      const key = e ? principalKey(e.principalType, e.principalId) : null;
      const name = key
        ? (this.state.membersByPrincipal[key]?.displayName ?? this._names[key])
        : null;
      return { deviceId, principalKey: key, name: name ?? 'участник' };
    });
    const kept = new Map((entry.copies ?? [])
      .filter((c) => c.cipher)
      .map((c) => [c.recipientDeviceId, c]));
    entry.copies = entries.map((e) => {
      if (blockedIds.includes(e.deviceId)) {
        // BLOCKED_KEYS — на КОПИЮ: видно, кто именно остановил пачку
        return { recipientDeviceId: e.deviceId, cipher: null, state: 'blocked_keys' };
      }
      return kept.get(e.deviceId)
        ?? { recipientDeviceId: e.deviceId, cipher: null, state: 'pending' };
    });
    entry.blocked = blocked;
    const names = [...new Set(blocked.map((b) => b.name))].join(', ');
    return this._failTerminal(entry,
      `Не доставлено: у ${names} кончились ключи шифрования. `
      + 'Попросите открыть общий чат — и повторите отправку.',
      { blocked }, 'blocked_keys');
  }

  /**
   * Комнаты нет или устройство не вправе: ждать нечего и повторять нечего.
   * Сообщение остаётся с текстом, а вкладка уходит в терминальное состояние —
   * иначе человек смотрит на «отправляется…» ровно столько, сколько у него
   * хватит терпения (ревью р.1, High).
   */
  async _terminalStop(entry, e) {
    entry.state = 'failed_terminal';
    entry.terminalReason = reasonOf(e);
    await this.store.putRoomOutbox(entry);
    // kind — экрану (терминальный вид), текст — человеку: он сейчас отправлял
    // сообщение, и «недоступен» ему не объясняет, что стало с текстом
    const fatal = roomBootstrapError(e);
    this._emit({
      phase: 'error',
      error: {
        ...fatal,
        text: terminalSendError(e) ?? fatal.text,
        messageId: entry.messageId,
      },
    });
    return false;
  }

  /**
   * Шаг, который обязан НЕ БРОСАТЬ наружу.
   *
   * 🔴 У `send` и «повторить» контракт один: ошибки идут через state, а не
   * реджектом. Отказ самого стора (квота, закрытая база) этот контракт
   * нарушал — страница получала unhandled rejection вместо плашки. Нашлось
   * тестом к правке р.2, а не ревью.
   */
  async _guard(fn, text) {
    try {
      return await fn();
    } catch (e) {
      if (e instanceof StoppedError || this.stopped) return undefined;
      // 🔴 Предохранитель не имеет права превращать КОНЕЦ в «повторите»
      // (ревью р.3, High): комнаты нет или устройство не вправе — это
      // терминальное состояние, а не обрыв связи.
      if (isFatal(e)) {
        this._emit({ phase: 'error', error: roomBootstrapError(e) });
        return undefined;
      }
      this._hiccup(text);
      return undefined;
    }
  }

  /** Восстановимый отказ: конверт остаётся в аутбоксе, «повторить» уместен. */
  _hiccup(text = 'Сообщение не ушло — проверьте связь и повторите') {
    // Терминальное состояние ретраябельной плашкой не затираем: предлагать
    // «повторите» там, где комнаты уже нет, — врать человеку (ревью р.3, High)
    if (this.state.phase === 'error') return false;
    this._emit({ error: { text, retriable: true } });
    return false;
  }

  /**
   * Ручной повтор ОДНОГО сообщения (кнопка у строки FAILED_TERMINAL).
   * Готовые шифротексты сохраняются, копии без шифротекста — выбрасываются:
   * под них состав перечитается заново.
   */
  retryMessage(messageId) {
    return this._enqueue(() => this._guard(async () => {
      if (!this.room) return;
      const list = await this.store.listRoomOutbox(this.room.id);
      const original = list.find((e) => e.messageId === messageId);
      if (!original) return;

      let entry = original;
      if (original.terminalReason === 'id_collision' || original.replacedBy) {
        // 🔴 Тем же id повтор ВЕЧЕН: сервер уже отдал его другому отправителю
        // (ревью р.1, Medium). Шифротексты смену id переживают — message_id в
        // них не входит, внутри только текст и время.
        //
        // 🔴 Имя замены рождается ОДИН раз и запоминается на старой строке
        // (ревью р.3, High). Иначе отказ стора между шагами оставляет старую
        // строку живой, человек жмёт «повторить» ещё раз — и рождается ВТОРАЯ
        // замена, то есть два одинаковых сообщения в комнате.
        const replacementId = original.replacedBy ?? newMessageId();
        if (!original.replacedBy) {
          original.replacedBy = replacementId;
          await this.store.putRoomOutbox(original);
        }
        entry = list.find((e) => e.messageId === replacementId)
          ?? { ...original, messageId: replacementId, replacedBy: null };
      }

      entry.state = 'pending';
      entry.attempts = 0;
      entry.blocked = [];
      entry.terminalReason = null;
      entry.copies = (entry.copies ?? []).filter((c) => c.cipher);
      // 🔴 Порядок обязателен: СНАЧАЛА новая запись, и только потом удаление
      // старой (ревью р.2, High). Наоборот — и отказ стора между двумя шагами
      // (квота, закрытая база) уносит текст человека вместе с готовыми
      // шифротекстами. Лишняя строка в аутбоксе переживается, потерянный
      // текст — нет.
      await this.store.putRoomOutbox(entry);
      if (entry.messageId !== messageId) {
        // 🔴 Порядок: СНАЧАЛА перевести предшественников на новую строку, и
        // только потом удалять старую (ревью р.5, High). Наоборот — и отказ
        // стора посреди перевода оставляет предшественника указывать в
        // пустоту: спрятать его больше нечем, он воскресает. Пока перевод не
        // прошёл, старая строка жива и продолжает его скрывать.
        for (const dead of list.filter((e) => e.replacedBy === messageId)) {
          dead.replacedBy = entry.messageId;
          await this.store.putRoomOutbox(dead);
        }
        await this.store.deleteRoomOutbox(messageId);
      }
      this._emit({ error: null });
      await this._deliver(entry);
      await this._loadHistory();
    }, 'Сообщение не удалось сохранить — повторите'));
  }

  /**
   * Перечитать состав комнаты и его ключи.
   *
   * Экран зовёт это, когда состав мог смениться, а отправки не было: догон
   * ростера не читает (он про сообщения), а отправка перечитывает его сама.
   * Без такой ручки смену ключа участника, который ничего не написал, владелец
   * увидел бы только при следующей отправке.
   */
  refresh() {
    return this._enqueue(() => this._guard(async () => {
      if (!this.room) return;
      await this._syncRoster();
      await this._syncDevices();
      await this._loadHistory();
    }, 'Не удалось перечитать состав чата — повторите'));
  }

  /** «Повторить» со страницы: дослать зависшее и добрать догон. */
  retryPending() {
    return this._enqueue(() => this._guard(async () => {
      await this._resendOutbox();
      await this._loadHistory();
    }, 'Не удалось дослать очередь — повторите'))
      .then(() => this.requestCatchUp());
  }

  /**
   * @returns {Promise<number>} сколько сообщений РЕАЛЬНО ушло.
   * 🔴 FAILED_TERMINAL не досылается: он ждёт руки человека, а автоповтор по
   * нему — тот самый бесконечный цикл, от которого мы поставили потолок.
   */
  async _resendOutbox() {
    if (!this.room) return 0;
    const pending = await this.store.listRoomOutbox(this.room.id);
    // Пустая очередь — и читать историю незачем (ревью р.7, Low)
    if (!pending.length) return 0;
    // 🔴 Строку, которая уже лежит в истории СВОИМ отправленным, досылать
    // нельзя — она осталась в аутбоксе только потому, что не прошла уборка
    // (ревью р.6, High). Фильтр по `out` обязателен: чужое входящее с тем же
    // message_id молча съело бы наш текст (ревью р.7, High).
    const delivered = new Set(
      (await this.store.listRoomMessages(this.room.id))
        .filter((r) => r.direction === 'out')
        .map((r) => r.messageId));
    let settled = 0;
    for (const entry of pending) {
      if (this.stopped) return settled;
      if (delivered.has(entry.messageId)) {
        try {
          // Предшественников — ПЕРВЫМИ: пока эта строка жива, она их скрывает,
          // а исчезни она раньше — им не на что опереться (ревью р.7, Medium)
          await this._dropPredecessors(entry.messageId);
          await this.store.deleteRoomOutbox(entry.messageId);
        } catch {
          /* уборка подождёт: показ и досыл и так смотрят на историю */
        }
        continue;
      }
      if (entry.state === 'failed_terminal') continue;
      if (await this._deliver(entry)) settled += 1;
    }
    return settled;
  }

  // ------------------------------------------------------------- приём

  /** Догон сериализуется той же очередью; повторный запрос схлопывается. */
  requestCatchUp() {
    if (this._catchUpQueued) return this.queue.tail;
    this._catchUpQueued = true;
    return this._enqueue(async () => {
      this._catchUpQueued = false;
      await this._catchUp();
    }).catch((e) => this._catchUpFailed(e));
  }

  /**
   * Отказ догона. Обрыв связи — плашка с повтором; но 404 и 403 значат, что
   * комнаты (или права на неё) больше нет, и повторять их бессмысленно:
   * вкладка обязана сказать это словами, а не крутить приём вечно.
   */
  _catchUpFailed(e) {
    if (isFatal(e)) {
      this._emit({ phase: 'error', error: roomBootstrapError(e) });
      return;
    }
    // через _hiccup, а не своим _emit: приговор («комнаты нет») ретраябельной
    // плашкой не затирается ни из одного тракта (ревью р.3, High)
    this._hiccup(
      'Приём сообщений прервался — повторите или подождите переподключения');
  }

  /**
   * Догон по РАЗРЕЖЕННОМУ ряду.
   *
   * 🔴 Условие выхода — `latest_seq`, а не пустая страница: свои сообщения
   * копий себе не имеют, чужие «до входа» не отдаются, ретеншн оставляет дыры.
   * Пустая страница при `after < latest_seq` означает дыру, и `after` обязан
   * прыгнуть СРАЗУ до `latest_seq` — иначе цикл не двигается вовсе и клиент
   * долбит сервер бесконечно (ровно эта ошибка была в первой редакции).
   */
  async _catchUp() {
    if (!this.room) return;
    const cursorKey = `room-cursor:${this.room.id}`;
    let after = (await this.store.getKV(cursorKey)) ?? 0;
    const started = after;
    this._seenSeq = Math.max(this._seenSeq, after);
    let latest = after;
    for (;;) {
      const before = after;
      const page = await this.api.roomCatchUp(this.room.id, after, CATCH_UP_PAGE);
      latest = page?.latest_seq ?? 0;
      const rows = page?.envelopes ?? [];
      if (rows.length) {
        for (const row of rows) {
          if (this.stopped) return;
          await this._applyEnvelope(row);
          after = Math.max(after, row.room_seq);
          this._seenSeq = Math.max(this._seenSeq, row.room_seq);
        }
      } else {
        after = Math.max(after, latest);
      }
      if (after >= latest) break;
      // Страховка от той же болезни с другой стороны: сервер отдал строки, но
      // ряд не сдвинулся. Не крутимся — выходим, курсор уедет на достигнутое,
      // следующий догон попробует снова.
      if (after <= before) break;
    }
    // 🔴 Порядок: расшифрованное УЖЕ сохранено (внутри _applyEnvelope), только
    // потом курсор серверу, и лишь затем локальный. Иначе обрыв между
    // «сдвинули курсор» и «сохранили» теряет сообщения навсегда: сервер по
    // курсору считает их доставленными и отдаёт ретеншну.
    if (after > started) {
      await this.api.postRoomCursor(this.room.id, after);
      await this.store.setKV(cursorKey, after);
    }
    await this._loadHistory();
  }

  /**
   * Одна входящая копия: доверие -> расшифровка -> запись.
   *
   * Отказ машины или стора ПРОБРАСЫВАЕТСЯ: конверт не «сгорает», курсор (ни
   * локальный, ни серверный) не двигается, догон повторится.
   */
  async _applyEnvelope(row) {
    await this._noteSenderDevice(row);
    // Строка конверта уходит клиенту ЦЕЛИКОМ (плюс seq в форме 1:1): машина
    // читает из неё cipher и вернёт всё, что нашла в расшифровке, — текст и
    // (С3) метаданные вложений.
    const [m] = await this.client.receiveEnvelopes([{
      ...row,
      seq: row.room_seq,
      message_id: row.message_id,
    }]);
    // Отправитель сверяется строго с тем, кем его назвал сервер: чужой
    // matrix-id внутри расшифровки = кривой конверт, а не сообщение.
    const expected = this.client.matrixIdOf(row.sender_type, row.sender_id);
    const trusted = m.plaintext !== null && m.senderMatrixId === expected;
    const text = trusted ? m.plaintext : null;
    // ATT С3: метаданные вложений — тем же правилом, что текст: только от
    // доверенного отправителя, форму каждого судит validMeta (содержимое
    // пришло от чужого клиента), кап тот же, что на сервере.
    const atts = trusted && Array.isArray(m.attachments)
      ? m.attachments.filter(validMeta).slice(0, MAX_ATTACHMENTS_PER_MESSAGE)
      : [];
    // WEB-16: цитата — та же посадка: только от доверенного отправителя и
    // только разрешённой формы. В комнате это важнее, чем в 1:1: снимок —
    // слова ОТПРАВИТЕЛЯ, а участников много и приписать фразу можно любому.
    const quote = trusted
      ? normalizeReplyTo(m.replyTo, { selfMessageId: row.message_id })
      : null;
    // Повторный догон гонит те же копии через машину ЕЩЁ раз: replay-дроп Olm
    // отдаёт null, и перезапись строки стёрла бы уже расшифрованный текст.
    // null никогда не затирает ни текст, ни вложения.
    const existing = await this.store.getRoomMessage(this.room.id, row.message_id);
    const kept = text ?? existing?.text ?? null;
    await this.store.putRoomMessage({
      roomId: this.room.id,
      messageId: row.message_id,
      roomSeq: row.room_seq,
      // МУ-1, срез А: копия может прийти от СЕБЯ — написанное с телефона
      // доезжает на компьютер. Это своя реплика, а не входящая: пузырь справа,
      // без имени, цитата «Вы». Очередь отправки её не спутает со своей
      // строкой — message_id у эха другой, его выдал другой браузер.
      direction: principalKey(row.sender_type, row.sender_id) === this.selfKey
        ? 'out' : 'in',
      text: kept,
      attachments: atts.length ? atts : existing?.attachments ?? null,
      // Тот же запрет затирания, что у текста и вложений: replay-дроп не
      // смеет стереть уже разобранную цитату.
      replyTo: quote ?? existing?.replyTo ?? null,
      // 🔴 Строку, которую расшифровать не вышло, курсор всё равно обгонит —
      // иначе одна битая копия заморозила бы приём НАВСЕГДА (Olm детерминирован:
      // что не расшифровалось раз, не расшифруется никогда). Цена честная:
      // такая строка обязана быть ВИДНА человеку как «не удалось расшифровать»,
      // а не молча пустой (ревью р.1, Blocker — согласны с половиной довода).
      decryptFailed: kept === null,
      sentAt: row.sent_at ?? null,
      senderType: row.sender_type,
      senderId: row.sender_id,
      senderDeviceId: row.sender_device_id,
    });
  }

  /**
   * Участник переустановил приложение и написал ПЕРВЫМ: 410 в комнате не
   * бывает вовсе, и единственный след нового устройства — `sender_device_id`
   * входящей копии. Расхождение с пином = событие доверия, разбираем ДО
   * записи текста в историю.
   */
  async _noteSenderDevice(row) {
    if (!row.sender_device_id) return;
    const key = principalKey(row.sender_type, row.sender_id);
    if (key === this.selfKey) return;
    const pinned = this.state.trustByPrincipal[key];
    if (!pinned?.deviceRowId || pinned.deviceRowId === row.sender_device_id) return;
    if (this._staleSenders.has(row.sender_device_id)) return;
    await this._syncRoster();
    await this._syncDevices();
    // Пометка ТОЛЬКО после успешного разбора: transient-отказ отсюда
    // пробрасывается, курсор не двигается, и повтор обязан снова прийти сюда,
    // а не проскочить мимо по «забаненному» id.
    this._staleSenders.add(row.sender_device_id);
  }

  // ------------------------------------------------------------- стоп

  /**
   * Остановить и дождаться, пока доедет уже начатая работа с машиной и
   * стором. Страница держит Web Lock до этого промиса: иначе следующий
   * контроллер откроет ТОТ ЖЕ Olm-стор, пока прежний ещё пишет.
   * Никогда не реджектится.
   */
  stop() {
    this.stopped = true;
    // Поток гасим ДО ожидания очереди: иначе пришедшее уведомление успело бы
    // поставить в неё догон, а «всё улеглось» отодвинулось бы на него.
    this.stream?.stop();
    return this.queue.drain().then(() => {
      try {
        this.store?.close();
      } catch {
        /* стор уже закрыт или не открылся — лок всё равно отпускаем */
      }
      // Крипто-стор закрывается вместе с машиной (Д-3, E3): иначе его
      // соединение живёт до перезагрузки и блокирует deleteDatabase.
      this.client?.close?.();
    });
  }
}
